import { useEffect, useId, useState, type FormEvent } from 'react';
import { Banner, Button, Dialog, RadioCard, Skeleton, TextField } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { loseFinancing, useLostReasons } from '../api';
import type { FinancingRecord } from '../types';
import styles from './dialogs.module.css';

export interface LoseDialogProps {
  open: boolean;
  financingId: string;
  numberLabel: string;
  onClose: () => void;
  onDone: (financing: FinancingRecord) => void;
}

/**
 * Finanzierungen 11 „Verloren setzen" (620 px): mandatory reason from `GET /financings/lost-reasons`, optional note
 * visible for advisor and admin → `POST /financings/:id/lose`.
 */
export function LoseDialog({ open, financingId, numberLabel, onClose, onDone }: LoseDialogProps) {
  const formId = useId();
  const reasons = useLostReasons(open);
  const [reasonId, setReasonId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReasonId(null);
    setNote('');
    setError(null);
    setBanner(null);
  }, [open, financingId]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    if (reasonId === null) {
      setError('Bitte wählen Sie einen Grund.');
      return;
    }
    setPending(true);
    setBanner(null);
    try {
      const body: { lostReasonId: string; note?: string } = { lostReasonId: reasonId };
      if (note.trim()) body.note = note.trim();
      onDone(await loseFinancing(financingId, body));
    } catch (cause) {
      if (cause instanceof ApiError && cause.fields['lostReasonId']) setError(cause.fields['lostReasonId']);
      else setBanner(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={620}
      eyebrow="Verloren setzen"
      title={`Finanzierung ${numberLabel} beenden`}
      subtitle="Die Finanzierung verlässt das Board. Der Immobilien-Deal bleibt, wie er ist, und zeigt einen Hinweis."
      footerNote="Provision entfällt · Wiederöffnen durch Admin"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending}>
            Auf verloren setzen
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.stack} onSubmit={submit} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {reasons.isError ? <Banner tone="danger">{errorMessage(reasons.error)}</Banner> : null}
        <fieldset className={styles.group} aria-describedby={error ? `${formId}-error` : undefined}>
          <legend className={styles.legend}>Grund (Pflicht)</legend>
          {reasons.isPending ? <Skeleton variant="block" height={96} /> : null}
          <div className={styles.grid2}>
            {reasons.data?.items.map((reason) => (
              <RadioCard
                key={reason.id}
                name={`${formId}-reason`}
                title={reason.name}
                value={reason.id}
                checked={reasonId === reason.id}
                onChange={() => {
                  setReasonId(reason.id);
                  setError(null);
                }}
              />
            ))}
          </div>
          {error ? (
            <span id={`${formId}-error`} className={styles.error} role="alert">
              {error}
            </span>
          ) : null}
        </fieldset>
        <TextField
          label="Notiz"
          value={note}
          hint="Sichtbar für Berater und Admin"
          onChange={(event) => setNote(event.target.value)}
        />
      </form>
    </Dialog>
  );
}
